import { DatePipe } from '@angular/common';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProfileModalService } from 'src/app/services/profile-modal.service';
import { HttpClient } from '@angular/common/http';
import { SecureSessionService } from 'src/app/services/secure-session.service';
import { CommonService } from 'src/app/services/common.service';
import { Subscription, combineLatest } from 'rxjs';
import { ConfigService } from 'src/app/services/config.service';

@Component({
  selector: 'app-profile-modal',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [DatePipe] 
})
export class ProfileModalComponent implements OnInit, OnDestroy {
  
  userProfile: any = {};
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;

  sessionData: any;
  adminId: any;

  isEditMode = false;
  profileForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  picture: string = '';
  
  private subscriptions = new Subscription();
  
  constructor(
    public modalService: ProfileModalService,
    private fb: FormBuilder,
    private session: SecureSessionService,
    private http: HttpClient,
    private _common: CommonService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private _config: ConfigService,
  ) {
    this.profileForm = this.fb.group({
      username: ['', Validators.required],
      dateOfBirth: ['', Validators.required],
      staffId: [''],
      phone: ['', Validators.required],
      designation: [''],
      alternatePhone: ['', Validators.required],
      address: ['', Validators.required],
      picture: ['']
    });
  }

  ngOnInit(): void {
    // Subscribe to modal opened events to load fresh data
    this.subscriptions.add(
      this.modalService.modalOpened$.subscribe(() => {
        this.loadUserProfile();
      })
    );

    // Subscribe to profile data changes
    this.subscriptions.add(
      this.modalService.profileData$.subscribe((data) => {
        if (data) {
          this.processProfileData(data);
        }
      })
    );

    // Subscribe to loading state
    this.subscriptions.add(
      this.modalService.loading$.subscribe((loading) => {
        this.isLoading = loading;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to error state
    this.subscriptions.add(
      this.modalService.error$.subscribe((error) => {
        this.errorMessage = error;
        if (error) {
          this._common.openSnackBarError(error);
        }
        this.cdr.detectChanges();
      })
    );

    // Load data initially if modal is already open
    if (this.modalService.isOpen) {
      this.loadUserProfile();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private getSessionData(): boolean {
    try {
      const sessionStr = this.session.getSession();
      if (!sessionStr) {
        console.error('No session data found');
        this._common.openSnackBarError('Session expired. Please log in again.');
        return false;
      }

      this.sessionData = JSON.parse(sessionStr);
      
      if (!this.sessionData || !Array.isArray(this.sessionData) || this.sessionData.length === 0) {
        console.error('Invalid or empty session data');
        this._common.openSnackBarError('Invalid session data. Please log in again.');
        return false;
      }

      if (!this.sessionData[0].id) {
        console.error('No user ID found in session data');
        this._common.openSnackBarError('User ID not found. Please log in again.');
        return false;
      }

      this.adminId = this.sessionData[0].id;
      return true;
    } catch (error) {
      console.error('Error parsing session data:', error);
      this._common.openSnackBarError('Error reading session data. Please log in again.');
      return false;
    }
  }

  private loadUserProfile(): void {
    // Get fresh session data each time
    if (!this.getSessionData()) {
      return;
    }

    // Use the service to fetch profile data
    this.subscriptions.add(
      this.modalService.fetchAdminProfile(this.adminId).subscribe()
    );
  }

  private processProfileData(data: any): void {
    console.log('Processing profile data:', data);
    const formattedDate = data.dob ? 
    this.datePipe.transform(new Date(data.dob), 'dd-MM-yyyy') : 'N/A';

      // this.datePipe.transform(new Date(data.dateofBirth), 'dd-MM-yyyy') : 'N/A';
        
    this.userProfile = {
      username: this.concatenateName(data.firstName, data.middleName, data.lastName),
      dateOfBirth: formattedDate || 'N/A',
      staffId: data.employeeId || 'N/A',
      mobile: data.phoneNumber || 'N/A',
      alternatePhone: data.alternatePhone || 'N/A',
      designation: data.designationName || 'N/A',
      address: data.permanentAddress || 'N/A',
      // Fix this line - use _imgurl instead of _baseurl
      profileImage: `${this._config._imgurl}/${data.profileImage}`
    };
        
    this.cdr.detectChanges();
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
    this.cdr.detectChanges();
  }

  onImgError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/blank-profile.webp';
  }

  concatenateName(first: string | null, middle: string | null, last: string | null): string {
    return [first, middle, last].filter(Boolean).join(' ') || 'N/A';
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    if (this.isEditMode) {
      this.cancelEdit();
    } else {
      this.modalService.close();
    }
  }

  closeModal(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      if (this.isEditMode) {
        this.cancelEdit();
      }
      this.modalService.close();
      this.isEditMode = false;
      this.resetImageSelection();
    }
  }

  handleManualClose(event: MouseEvent): void {
    this.modalService.close();
    this.isEditMode = false; 
    this.resetImageSelection();
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      this.profileForm.setValue({
        username: this.userProfile.username,
        dateOfBirth: this.userProfile.dateOfBirth,
        staffId: this.userProfile.staffId,
        phone: this.userProfile.mobile,
        designation: this.userProfile.designation,
        alternatePhone: this.userProfile.alternatePhone,
        address: this.userProfile.address,
        picture: ''
      });
    }
    this.isEditMode = !this.isEditMode;
  }

  cancelEdit() {
    this.isEditMode = false;
    this.resetImageSelection();
    // Reset the form to original values
    this.profileForm.setValue({
        username: this.userProfile.username,
        dateOfBirth: this.userProfile.dateOfBirth,
        staffId: this.userProfile.staffId,
        phone: this.userProfile.mobile,
        designation: this.userProfile.designation,
        alternatePhone: this.userProfile.alternatePhone,
        address: this.userProfile.address,
        picture: ''
    });
  }

  resetImageSelection(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  isValidDateFormat(dateString: string): boolean {
    if (!dateString || dateString === 'N/A') return false;
    
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    
    if (!dateRegex.test(dateString)) {
      return false;
    }
    
    const parts = dateString.split('/');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    return date.getDate() === day && 
           date.getMonth() === month && 
           date.getFullYear() === year;
  }

  // In profile-modal.component.ts - saveProfile() method
  saveProfile() {
    if (this.profileForm.valid) {
      const formValues = this.profileForm.value;
            
      if (!this.isValidDateFormat(formValues.dateOfBirth)) {
        this._common.openSnackBarError('Date must be in DD-MM-YYYY format');
        return;
      }
            
      // Get fresh session data before saving
      if (!this.getSessionData()) {
        return;
      }

      const updateUrl = `${this._config._url}AdminLogin/update-admin/${this.adminId}`;
      const nameParts = formValues.username.trim().split(' ');
      const firstName = nameParts[0] || 'N/A';
      const middleName = nameParts.length === 3 ? nameParts[1] : '';
      const lastName = nameParts.length === 3 ? nameParts[2] : nameParts[1] || '';

      let apiDateFormat = '2000-01-01';
      if (formValues.dateOfBirth && formValues.dateOfBirth !== 'N/A') {
        const parts = formValues.dateOfBirth.split('/');
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          apiDateFormat = `${year}-${month}-${day}`;
        }
      }

      const formData = new FormData();
      formData.append('id', this.adminId);
      formData.append('firstName', firstName);
      formData.append('middleName', middleName);
      formData.append('lastName', lastName);
      formData.append('dob', apiDateFormat);
      formData.append('mobile', formValues.phone || '0000000000');
      formData.append('AlternatePhone', formValues.alternatePhone || '0000000000');
      formData.append('address', formValues.address || 'N/A');

      if (this.selectedFile) {
        formData.append('profileImage', this.selectedFile);
      }

      this.http.put(updateUrl, formData).subscribe({
      next: (res: any) => {
        this.isEditMode = false;
        
        // Construct the new image URL properly
        let newImageUrl = this.userProfile.profileImage;
        
        if (this.selectedFile && res.updatedAdmin?.profileImageName) {
          // Make sure the URL format matches what admin component expects
          newImageUrl = `${this._config._imgurl}/${res.updatedAdmin.profileImageName}`;
        }

        this.userProfile = {
          username: `${firstName} ${middleName} ${lastName}`.trim(),
          dateOfBirth: formValues.dateOfBirth,
          staffId: this.userProfile.staffId,
          mobile: formValues.phone,
          designation: this.userProfile.designation,
          alternatePhone: formValues.alternatePhone,
          address: formValues.address,
          profileImage: newImageUrl
        };

        this.resetImageSelection();
        this.cdr.detectChanges();
        this._common.openSnackBar('Admin Details Updated');

        // Emit the updated profile image URL - ALWAYS emit, not just when file is selected
        console.log('Emitting updated image URL:', newImageUrl);
        this.modalService.updateProfileImage(newImageUrl);

        // Refresh profile data after successful update
        this.loadUserProfile();
      },
      error: (err) => {
        console.error('Update failed:', err);
        const message = this.extractErrorMessage(err);
        this._common.openSnackBarError(message);
      }
    });
    } else {
      this.markFormGroupTouched(this.profileForm);
      this._common.openSnackBarError('Please fill all required fields correctly');
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private extractErrorMessage(error: any): string {
    if (!error?.error) {
      return 'An unknown error occurred.';
    }

    if (typeof error.error.message === 'string') {
      return error.error.message;
    }

    if (error.error.errors && typeof error.error.errors === 'object') {
      const validationMessages = Object.values(error.error.errors)
        .map(err => Array.isArray(err) ? err.join(', ') : err)
        .join('\n');
      return validationMessages || 'Validation error occurred.';
    }

    return error.message || 'An unexpected error occurred.';
  }
}